import { Suspense, useEffect, useState } from "react";
import PreviewTermsGate from "./PreviewTermsGate";
import PreviewFeedbackForm from "./PreviewFeedbackForm";
import PreviewDesktopFeedbackForm from "./PreviewDesktopFeedbackForm";
import { getSolutionOverview } from "./solutionRegistry";
import WatermarkOverlay from "../../components/common/watermark-overlay/WatermarkOverlay";
import { usePageLogger } from "../../hooks/usePageLogger";
import { useContentProtection } from "../../hooks/useContentProtection";
import { storePreviewToken, clearStoredPreviewToken } from "../../contexts/PreviewSessionContext";
import { firstAllowedPath } from "../../lib/preview-pages";

interface TokenData {
  share_kind: "solution" | "multi_page";
  allowed_pages: string[] | null;
  skip_terms: boolean;
  label: string | null;
  solution_slug: string | null;
  solution_name: string | null;
  attributed_email: string;
  attributed_company: string | null;
  attributed_name: string | null;
  terms_accepted: boolean;
}

type PageState = "loading" | "error" | "terms" | "content" | "redirecting";

export default function PreviewPage() {
  const token = window.location.pathname.split("/preview/")[1]?.split("/")[0] ?? "";

  const [pageState, setPageState] = useState<PageState>("loading");
  const [tokenData, setTokenData] = useState<TokenData | null>(null);
  const [errorMsg, setErrorMsg] = useState("");

  usePageLogger({
    userEmail: tokenData?.attributed_email ?? null,
    userType: "preview",
  });
  useContentProtection();

  useEffect(() => {
    if (!token) {
      setErrorMsg("Invalid preview link.");
      setPageState("error");
      return;
    }
    fetch(`/api/preview/validate/${token}`)
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          setErrorMsg(data.error ?? "This preview link is not available.");
          setPageState("error");
          clearStoredPreviewToken();
          return;
        }
        setTokenData(data);

        if (data.share_kind === "multi_page") {
          // Hand off to the main app: persist token, then bounce to the
          // first allowed page (or terms gate).
          storePreviewToken(token);
          if (!data.skip_terms && !data.terms_accepted) {
            setPageState("terms");
            return;
          }
          setPageState("redirecting");
          window.location.replace(firstAllowedPath(data.allowed_pages || []));
          return;
        }

        // Solution-kind: existing behavior.
        setPageState(data.terms_accepted ? "content" : "terms");
      })
      .catch(() => {
        setErrorMsg("Unable to load preview. Please try again.");
        setPageState("error");
      });
  }, [token]);

  if (pageState === "loading" || pageState === "redirecting") {
    return (
      <div className="fixed inset-0 bg-[#05050F] flex items-center justify-center">
        <div className="text-[#6B96D6] text-sm">Loading preview…</div>
      </div>
    );
  }

  if (pageState === "error") {
    return (
      <div className="fixed inset-0 bg-[#05050F] flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <img
            src="https://cdn.prod.website-files.com/6656cc98d077ac69272f468d/666c3d6f5a0ea57d121a1846_Logo.webp"
            alt="FixFinder"
            className="h-9 mx-auto mb-6"
          />
          <h1 className="text-white text-xl font-semibold mb-3">Preview Unavailable</h1>
          <p className="text-[#6B96D6] text-sm">{errorMsg}</p>
        </div>
      </div>
    );
  }

  if (pageState === "terms" && tokenData) {
    return (
      <PreviewTermsGate
        token={token}
        solutionName={tokenData.solution_name ?? tokenData.label ?? "FixFinder Preview"}
        attributedName={tokenData.attributed_name}
        onAccepted={() => {
          if (tokenData.share_kind === "multi_page") {
            setPageState("redirecting");
            window.location.replace(firstAllowedPath(tokenData.allowed_pages || []));
            return;
          }
          setPageState("content");
        }}
      />
    );
  }

  if (pageState === "content" && tokenData && tokenData.share_kind === "solution") {
    const OverviewComponent = tokenData.solution_slug
      ? getSolutionOverview(tokenData.solution_slug)
      : null;
    return (
      <div
        id="main-content"
        className="fixed inset-0 bg-[#05050F] overflow-y-auto"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        <WatermarkOverlay email={tokenData.attributed_email} />
        <div className="max-w-5xl mx-auto px-4 pb-20">
          <header className="py-6 flex items-center justify-between border-b border-white/10 mb-8">
            <img
              src="https://cdn.prod.website-files.com/6656cc98d077ac69272f468d/666c3d6f5a0ea57d121a1846_Logo.webp"
              alt="FixFinder"
              className="h-8"
            />
            <div className="text-right">
              <p className="text-[#DDEBFF] text-sm font-medium">{tokenData.solution_name}</p>
              {tokenData.attributed_company && (
                <p className="text-[#6B96D6] text-xs">{tokenData.attributed_company}</p>
              )}
            </div>
          </header>

          <section className="mb-16">
            {OverviewComponent ? (
              <Suspense
                fallback={
                  <div className="text-center py-20 text-[#6B96D6] text-sm">
                    Loading content…
                  </div>
                }
              >
                <OverviewComponent />
              </Suspense>
            ) : (
              <div className="text-center py-20 text-[#6B96D6] text-sm">
                Content unavailable for this solution.
              </div>
            )}
          </section>

          <div className="border-t border-white/10 pt-12">
            {tokenData.solution_slug === "desktop-app" ? (
              <PreviewDesktopFeedbackForm token={token} solutionName={tokenData.solution_name ?? ""} />
            ) : (
              <PreviewFeedbackForm token={token} solutionName={tokenData.solution_name ?? ""} />
            )}
          </div>

          <footer className="mt-16 pt-6 border-t border-white/10 text-center">
            <p className="text-[#6B96D640] text-xs">
              © 2026 FixFinder Inc. — Confidential Preview
            </p>
          </footer>
        </div>
      </div>
    );
  }

  return null;
}
